import React, { useRef } from 'react';
import { ImageEditorComponent } from '@syncfusion/ej2-react-image-editor';

const ImageEditor = () => {
  const imageEditorRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && imageEditorRef.current) {
      const reader = new FileReader();
      reader.onload = (e) => {
        imageEditorRef.current.open(e.target.result);
        // Add a small delay to ensure the image is fully loaded before adjusting
        setTimeout(() => {
          if (imageEditorRef.current) {
            // Reset zoom to fit the image properly
            imageEditorRef.current.zoom(100);
            // Clear any selection that might be causing positioning issues
            imageEditorRef.current.clearSelection();
          }
        }, 100);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (imageEditorRef.current) {
      imageEditorRef.current.export('JPEG', 'edited-image');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-4">Professional Image Editor</h1>
          <p className="text-gray-300 mb-6">
            Edit and enhance your images with professional tools including crop, rotate, filters, annotations, and more.
          </p>
          
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="file-input"
              />
              <label 
                htmlFor="file-input" 
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg cursor-pointer transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Choose Image
              </label>
            </div>
            
            <button 
              onClick={handleSave} 
              className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save Image
            </button>
          </div>
        </div>

        {/* Image Editor */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Editor Canvas</h2>
            <p className="text-sm text-gray-400 mt-1">
              Upload an image to start editing. Use the toolbar to access editing tools.
            </p>
          </div>
          
          <div className="image-editor-container bg-gray-900">
            <ImageEditorComponent
              ref={imageEditorRef}
              id="image-editor"
              height="600px"
              width="100%"
              created={() => {
                console.log('Image Editor created successfully');
              }}
              fileOpened={(args) => {
                console.log('File opened:', args);
                // Ensure proper positioning when file is opened
                setTimeout(() => {
                  if (imageEditorRef.current) {
                    // Reset any transformations and center the image
                    imageEditorRef.current.reset();
                    imageEditorRef.current.zoom(100);
                  }
                }, 200);
              }}
            />
          </div>
        </div>

        {/* Feature Information */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Crop & Resize</h3>
            <p className="text-gray-400 text-sm">
              Crop images to custom dimensions or preset aspect ratios. Resize images while maintaining quality.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v1M7 4V3a1 1 0 011-1h8a1 1 0 011 1v1m-9 4h10m-5 0V4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Filters & Effects</h3>
            <p className="text-gray-400 text-sm">
              Apply professional filters and effects to enhance your images with one-click presets.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Annotations</h3>
            <p className="text-gray-400 text-sm">
              Add text, shapes, arrows, and drawings to your images with customizable styles.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Transform</h3>
            <p className="text-gray-400 text-sm">
              Rotate, flip, and transform images with precise controls and real-time preview.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor; 